import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface Exchange {
  id: string;
  name: string;
  year_established: number;
  country: string;
  description: string;
  url: string;
  image: string;
  trust_score: number;
  trust_score_rank: number;
  trade_volume_24h_btc: number;
  trade_volume_24h_btc_normalized: number;
  last_updated: number;
}

interface ExchangeDB extends DBSchema {
  exchanges: {
    key: string;
    value: Exchange;
    indexes: { 'by-trust-score': number };
  };
  metadata: {
    key: string;
    value: {
      last_updated: number;
    };
  };
}

let db: IDBPDatabase<ExchangeDB>;

export async function initDB() {
  if (db) return db;
  
  try {
    db = await openDB<ExchangeDB>('exchange-db', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('exchanges')) {
          const exchangeStore = db.createObjectStore('exchanges', { keyPath: 'id' });
          exchangeStore.createIndex('by-trust-score', 'trust_score');
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata');
        }
      },
    });
    return db;
  } catch (error) {
    console.warn('Failed to initialize IndexedDB for exchanges, falling back to memory storage');
    throw error;
  }
}

export async function updateExchanges() {
  try {
    await initDB();
    const metadata = await db.get('metadata', 'last_update');
    const now = Date.now();
    const ONE_HOUR = 60 * 60 * 1000;

    // Only update if more than an hour has passed or no previous data exists
    if (!metadata || now - metadata.last_updated > ONE_HOUR) {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/exchanges?per_page=100',
        {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const exchanges = await response.json();
      
      // Start a transaction
      const tx = db.transaction(['exchanges', 'metadata'], 'readwrite');
      
      try {
        // Clear existing exchanges
        await tx.objectStore('exchanges').clear();
        
        // Add new exchanges
        for (const exchange of exchanges) {
          await tx.objectStore('exchanges').add({
            ...exchange,
            last_updated: now
          });
        }
        
        // Update metadata
        await tx.objectStore('metadata').put({ last_updated: now }, 'last_update');
        
        // Commit transaction
        await tx.done;
        
        return exchanges;
      } catch (error) {
        // If transaction fails, abort it
        tx.abort();
        throw error;
      }
    }
    
    // Return cached exchanges if within the hour
    return await getAllExchanges();
  } catch (error) {
    console.warn('Error updating exchanges, falling back to cached data');
    const cachedExchanges = await getAllExchanges();
    if (cachedExchanges.length > 0) {
      return cachedExchanges;
    }
    throw error;
  }
}

export async function getAllExchanges(): Promise<Exchange[]> {
  try {
    await initDB();
    return await db.getAllFromIndex('exchanges', 'by-trust-score');
  } catch (error) {
    console.warn('Error getting exchanges from IndexedDB');
    throw error;
  }
}

export async function getExchangeById(id: string): Promise<Exchange | undefined> {
  try {
    await initDB();
    return await db.get('exchanges', id);
  } catch (error) {
    console.warn('Error getting exchange from IndexedDB');
    throw error;
  }
}

export async function getLastUpdateTime(): Promise<number | null> {
  try {
    await initDB();
    const metadata = await db.get('metadata', 'last_update');
    return metadata?.last_updated || null;
  } catch (error) {
    console.warn('Error getting last update time from IndexedDB');
    return null;
  }
}